const PENDING_KEY = 'angelcam-connect-pending';

export function base64UrlEncode(bytes) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function randomString(byteLength = 32) {
  return base64UrlEncode(crypto.getRandomValues(new Uint8Array(byteLength)));
}

export async function codeChallenge(verifier) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

export async function startAuthorization(config, storage, { pageParams = new URLSearchParams(), signUp = false } = {}) {
  const verifier = randomString(64);
  const state = randomString(16);
  storage.setItem(PENDING_KEY, JSON.stringify({ verifier, state }));

  const url = new URL(config.authorizeUrl);
  url.search = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    state,
    code_challenge: await codeChallenge(verifier),
    code_challenge_method: 'S256',
  }).toString();

  const utm = UTM_PARAMS.filter((name) => pageParams.get(name));
  if (!signUp && !utm.length) return url.toString();

  const account = new URL(signUp ? config.signupUrl : config.loginUrl);
  account.search = new URLSearchParams(signUp ? config.signupParams : {}).toString();
  account.searchParams.set('next', url.pathname + url.search);
  for (const name of utm) account.searchParams.set(name, pageParams.get(name));
  return account.toString();
}

export class ConnectError extends Error {
  constructor(message, { retry = true } = {}) {
    super(message);
    this.retry = retry;
  }
}

export function isCallback(params) {
  return params.has('code') || params.has('error');
}

export async function completeAuthorization(config, params, storage, fetchImpl = fetch) {
  const pending = JSON.parse(storage.getItem(PENDING_KEY) || 'null');
  storage.removeItem(PENDING_KEY);

  if (params.get('error') === 'access_denied') {
    throw new ConnectError('You cancelled the connection. No access was granted to your cameras.');
  }
  if (params.has('error')) {
    throw new ConnectError(`Angelcam returned an error: ${params.get('error_description') || params.get('error')}`);
  }
  if (!pending || !params.get('state') || params.get('state') !== pending.state) {
    throw new ConnectError('This connection link is invalid or has expired. Please start again.');
  }

  let response;
  try {
    response = await fetchImpl(config.tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code: params.get('code'),
        redirect_uri: config.redirectUri,
        client_id: config.clientId,
        code_verifier: pending.verifier,
      }),
    });
  } catch {
    throw new ConnectError('Could not reach Angelcam. Check your internet connection and try again.');
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.refresh_token) {
    throw new ConnectError(`Angelcam could not complete the connection (${payload.error || `HTTP ${response.status}`}).`);
  }
  return payload.refresh_token;
}
